import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Phone, Brain, ChevronRight } from 'lucide-react';
import { Button } from '../UI/Button';
import { doctorInfo } from '../../data/content';

export const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Check if we are on the home page
  const isHome = location.pathname === '/';
  
  // Determine if navbar should be transparent
  // Only transparent if on Home page AND not scrolled
  const isTransparent = isHome && !scrolled;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Media', path: '/media' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <nav 
      className={`fixed w-full z-50 transition-all duration-300 border-b ${
        isTransparent 
          ? 'bg-transparent border-transparent py-4' 
          : 'bg-white/95 backdrop-blur-md shadow-lg border-blue-100 py-2'
      }`}
    >
      {/* Top Accent Line for Neurology Theme */}
      {!isTransparent && (
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-600 via-teal-500 to-blue-600"></div>
      )}

      <div className="container mx-auto px-4 md:px-6">
        <div className="flex justify-between items-center">
          {/* Logo Section */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className={`p-2 rounded-xl transition-all duration-300 ${
              isTransparent 
                ? 'bg-white/10 backdrop-blur-sm text-white group-hover:bg-white/20' 
                : 'bg-gradient-to-br from-blue-600 to-teal-500 text-white shadow-lg group-hover:shadow-blue-500/30'
            }`}>
              <Brain size={26} className="animate-pulse-slow" />
            </div>
            <div className="flex flex-col">
              <span className={`font-bold text-xl leading-none tracking-tight ${
                isTransparent ? 'text-white' : 'text-slate-800'
              }`}>
                {doctorInfo.name}
              </span>
              <span className={`text-xs font-medium tracking-wider uppercase mt-0.5 ${
                isTransparent ? 'text-blue-100' : 'text-blue-600'
              }`}>
                {doctorInfo.title}
              </span>
            </div>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={`relative px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 group ${
                  location.pathname === link.path 
                    ? (isTransparent ? 'bg-white/20 text-white' : 'bg-blue-50 text-blue-700')
                    : (isTransparent ? 'text-white/90 hover:text-white hover:bg-white/10' : 'text-slate-600 hover:text-blue-600 hover:bg-blue-50/50')
                }`}
              >
                {link.name}
                {/* Active Dot Indicator */}
                {location.pathname === link.path && (
                  <span className={`absolute bottom-1.5 left-1/2 transform -translate-x-1/2 w-1 h-1 rounded-full ${
                    isTransparent ? 'bg-white' : 'bg-blue-600'
                  }`}></span>
                )}
              </Link>
            ))}
            
            <div className="ml-4 pl-4 border-l border-gray-200/20">
              <a 
                href={doctorInfo.social.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button 
                  size="sm" 
                  variant={isTransparent ? 'secondary' : 'primary'}
                  className={!isTransparent ? 'bg-[#25D366] hover:bg-[#20bd5a] text-white shadow-md hover:shadow-lg border-0' : 'bg-white/20 text-white hover:bg-white/30 border-0'}
                >
                  <img src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" alt="WhatsApp" className="w-4 h-4 mr-2" />
                  Chat on WhatsApp
                </Button>
              </a>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className={`md:hidden p-2 rounded-lg transition-colors ${
              isTransparent ? 'text-white hover:bg-white/10' : 'text-slate-700 hover:bg-slate-100'
            }`}
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu Overlay */}
        {isOpen && (
          <div className="md:hidden absolute top-full left-0 w-full bg-white/98 backdrop-blur-xl shadow-xl border-t border-gray-100 flex flex-col animate-in slide-in-from-top-5 z-50">
            <div className="p-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`flex items-center justify-between p-4 rounded-xl transition-all ${
                    location.pathname === link.path 
                      ? 'bg-blue-50 text-blue-700 font-semibold' 
                      : 'text-slate-600 hover:bg-gray-50'
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  <span className="text-lg">{link.name}</span>
                  {location.pathname === link.path && <ChevronRight size={20} />}
                </Link>
              ))}
              <div className="pt-4 mt-2 border-t border-gray-100">
                <a 
                  href={doctorInfo.social.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full"
                >
                  <Button className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-white border-0 py-6 text-lg">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" alt="WhatsApp" className="w-5 h-5 mr-2" />
                    Chat on WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
